import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import { ChevronLeft } from "lucide-react";
import Shield from "../../../assets/aftersalesservice_icons/shield.svg";
import Container from "../../../assets/aftersalesservice_icons/container.svg";
import Human from "../../../assets/aftersalesservice_icons/human.svg";
import FallbackImg from "../../../components/FallbackImg";
import ApiService from "../../../components/Api_services";
import RegisteredReceived from "../registeredreceived/RegisteredReceived";
import MultiPurpose from "../aftersales_multipurpose/MultiPurpose";
import {
  PHONE_COUNTRY_OPTIONS,
  inferCountryFromName,
  splitPhoneInput,
  isValidRegionalPhone,
  detectCountryFromFullNumber,
} from "../warrantyPhoneUtils";
import { useLanguage } from "../LanguageContext";
import {
  buildAfterSalesQuery,
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
} from "../serialTypeUtils";

const RegisterSerialNo = () => {
  const { trans, lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sn = atob(searchParams.get("sn") || "");
  const getSerialNo = () => sn;
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);

  const [customerData, setCustomerData] = useState("");
  const [warrantyInfo, setWarrantyInfo] = useState({
    warrantyitemlist: "",
    salesorder: "",
    custname: "",
    custtelno: "",
    emailadd: "",
    country: "",
    state: "",
    city: "",
    postcode: "",
    street: "",
    invoiceno: "",
    purchasedate: "",
  });
  const [loading, setLoading] = useState(false);
  const [notifMessage, setNotifMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [agreeWarrantyPolicy, setAgreeWarrantyPolicy] = useState(false);
  const [warrantyPolicyPdf, setWarrantyPolicyPdf] = useState(null);
  const [errors, setErrors] = useState({
    custname: "",
    custtelno: "",
  });
  const [cleanPhoneNumber, setCleanPhoneNumber] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("MY");

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("en-MY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchWarrantyInfo = async () => {
      const serialNo = serialNoForApi;

      try {
        const response = await ApiService.getWarrantyInfo(serialNo, serialType);
        // console.log("warranty info:", response);

        if (response && response.status === 200) {
          const items = response.data?.warranty_item_list || [];
          // ensure each item has serial_no
          const normalizedItems = (Array.isArray(items) ? items : []).map(
            (it) => {
              const { item_warranty_period, ...rest } = it || {};
              return {
                ...rest, // rest no longer contains item_warranty_period
                // map item_warranty_period -> warranty_day (ensure numeric)
                warranty_day:
                  rest?.warranty_day ??
                  (item_warranty_period != null
                    ? Number(item_warranty_period)
                    : 0),
              };
            },
          );

          const customer = response.data?.customer_data || null;
          const phoneRaw = customer?.phone_no || "";
          const defaultCountry = inferCountryFromName(customer?.country);
          const normalized = splitPhoneInput(phoneRaw, defaultCountry);
          setPhoneCountry(normalized.countryId || defaultCountry);
          setCleanPhoneNumber(normalized.digit_only);

          const updateFields = {
            warrantyitemlist: normalizedItems,
            salesorder: customer?.sales_order || "",
            custname: customer?.receiver_name || "",
            custtelno: normalized.nationalDisplay || normalized.national || "",
            country: customer?.country || "",
            state: customer?.province || "",
            city: customer?.city || "",
            postcode: customer?.zip_code || "",
            street: customer?.address || "",
            invoiceno: customer?.shopee_order_id || "",
            purchasedate: customer?.purchase_date || "",
          };

          setWarrantyInfo((prev) => ({ ...prev, ...updateFields }));
          setCustomerData(response.data?.customer_data);
        }
      } catch (error) {
        console.error("Error fetching warranty info:", error);
      }
    };

    fetchWarrantyInfo();
  }, [serialNoForApi, serialType]);

  const warrantyItems = Array.isArray(warrantyInfo.warrantyitemlist)
    ? warrantyInfo.warrantyitemlist
    : [];
  const itemId1 = warrantyItems[0]?.item_id;
  const itemId2 = warrantyItems[1]?.item_id;
  const countryCode = warrantyItems[0]?.country_code || "";

  useEffect(() => {
    const fetchWarrantyPolicyPdf = async () => {
      const item_id = [itemId1, itemId2].filter(Boolean).map(Number);
      if (item_id.length === 0) return;
      const apiLang = lang === "ch" ? "cn" : lang === "my" ? "ms" : lang;

      try {
        const response = await ApiService.getWarrantyPolicy(
          item_id,
          apiLang,
          countryCode,
        );
        if (response && response.status === 200) {
          setWarrantyPolicyPdf(response.data[0]?.pdf_file || null);
        } else {
          setWarrantyPolicyPdf(null);
          console.error("Error fetching warranty policy:", response.message);
        }
      } catch (error) {
        setWarrantyPolicyPdf(null);
        console.error("Error fetching warranty policy:", error);
      }
    };

    fetchWarrantyPolicyPdf();
  }, [itemId1, itemId2, countryCode, lang]);

  const openPdf = (e) => {
    e.preventDefault();
    if (warrantyPolicyPdf) window.open(warrantyPolicyPdf, "_blank");
  };

  useEffect(() => {
    if (customerData.shopee_order_id == "") {
      navigate(`/aftersalesservice${buildAfterSalesQuery(searchParams)}`);
    }
  }, [customerData]);

  useEffect(() => {
    const fetchRegisterStatus = async () => {
      const serialNo = serialNoForApi;

      try {
        const response = await ApiService.checkRegisterStatus(
          serialNo,
          serialType,
        );

        if (response) {
          if (response.status === 200) {
            navigate(
              `/aftersalesservice/${buildAfterSalesQuery(searchParams)}`,
            );
          }
        }
      } catch (error) {
        console.error("Error fetching register status:", error);
      }
    };

    fetchRegisterStatus();
  }, [serialNoForApi, serialType]);

  const handlePhoneInput = (value) => {
    const split = splitPhoneInput(value, phoneCountry);
    const digits = (value || "").replace(/[^\d]/g, "");
    if (detectCountryFromFullNumber(digits)) {
      setPhoneCountry(split.countryId);
    }
    setCleanPhoneNumber(split.digit_only);
    setWarrantyInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || "",
    }));
    setErrors((prev) => ({
      ...prev,
      custtelno: "",
    }));
  };

  const handlePhoneBlur = (value) => {
    const trimmed = value.trim();
    if (trimmed === "") return;

    const split = splitPhoneInput(trimmed, phoneCountry);
    if (detectCountryFromFullNumber(trimmed.replace(/[^\d]/g, ""))) {
      setPhoneCountry(split.countryId);
    }
    setCleanPhoneNumber(split.digit_only);
    setWarrantyInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || trimmed,
    }));
  };

  const handlePhoneCountryChange = (countryId) => {
    setPhoneCountry(countryId);
    if (!warrantyInfo.custtelno?.trim()) return;

    const split = splitPhoneInput(warrantyInfo.custtelno, countryId);
    setCleanPhoneNumber(split.digit_only);
    setWarrantyInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || prev.custtelno,
    }));
    setErrors((prev) => ({
      ...prev,
      custtelno: "",
    }));
  };

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);
    setNotifMessage("");

    let newErrors = { custname: "", custtelno: "" };
    let hasError = false;

    if (!warrantyInfo.custname) {
      newErrors.custname = "Please enter buyer name";
      hasError = true;
    }

    if (!warrantyInfo.custtelno) {
      newErrors.custtelno = "Please enter mobile number";
      hasError = true;
    } else if (!isValidRegionalPhone(warrantyInfo.custtelno, phoneCountry)) {
      newErrors.custtelno = "Invalid mobile number format for selected country";
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) {
      setLoading(false);
      return;
    }

    const submitPhone =
      cleanPhoneNumber ||
      splitPhoneInput(warrantyInfo.custtelno, phoneCountry).digit_only;

    // Prepare warranty_item_list with only required fields
    const warranty_item_list = warrantyInfo.warrantyitemlist.map((item) => {
      const mappedItem = {
        serial_no: item.serial_no,
      };

      // Only include warranty if it exists and has data
      if (item.warranty && item.warranty.length > 0) {
        mappedItem.warranty = item.warranty.map((w) => ({
          name: w.name,
          duration: w.duration,
        }));
      }

      return mappedItem;
    });

    const payload = {
      name: warrantyInfo.custname,
      phone: submitPhone,
      phone_country: phoneCountry,
      email: warrantyInfo.emailadd || "",
      // IMPORTANT: backend expects a JSON string for warranty_item_list
      warranty_item_list: JSON.stringify(warranty_item_list),
      serial_type: serialType,
    };

    try {
      const response = await ApiService.registerWarranty(payload);
      if (response && response.status === 200) {
        setShowSuccessMessage(true);
        // localStorage.setItem("justRegistered", "true");
      } else {
        console.error("Error submitting registration:", response);
        // Handle API error message if available
        const errorMessage =
          response?.message || "Failed to submit registration.";
        setNotifMessage(errorMessage);
        setShowMessage(true);
      }
    } catch (error) {
      console.error("Error submitting registration:", error);
      // Handle different types of errors
      let errorMessage = "Failed to submit registration.";

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setNotifMessage(errorMessage);
      setShowMessage(true);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   if (localStorage.getItem("justRegistered")) {
  //     setShowSuccessMessage(true);
  //   }
  // }, []);

  return (
    <AfterSalesLayout>
      <div className="as-register-serial-no-content">
        <div style={{ padding: "0 31.97px" }}>
          {/* ONE BIG CARD */}
          <div className="as-csn_main-card">
            {/* Blue gradient top of the card */}
            <div className="as-csn_header-card">
              <div
                className="as-csn_header-cancel"
                onClick={() =>
                  navigate(
                    `/aftersalesservice${buildAfterSalesQuery(searchParams)}`,
                  )
                }
              >
                <ChevronLeft size={16} />
                {trans.cancel}
              </div>
              <div className="as-csn_shield-icon">
                <FallbackImg
                  src={Shield}
                  alt="shield"
                  style={{ width: 80, height: 80 }}
                />
              </div>
              <div className="as-csn_header-card-title">{trans.air_con}</div>
              <div className="as-csn_header-card-sub">{trans.warr_reg}</div>
            </div>

            {/* White content area inside the same card */}
            <div className="as-csn_form-area">
              {/* FORM START */}
              <div id="form_detail">
                {/* Purchase Details Section */}
                <div className="as-csn_section-title">
                  <div className="as-csn_icon-box" style={{ paddingRight: 10 }}>
                    <FallbackImg src={Container} alt="container" />
                  </div>
                  <span className="as-csn_section-text">
                    {trans.purch_details}
                  </span>
                </div>

                <div className="as-csn_input-container">
                  <label className="as-csn_input-label">{trans.order_no}</label>
                  <input
                    type="text"
                    className="as-csn_input-value"
                    id="reg_inv_no"
                    name="reg_inv_no"
                    value={warrantyInfo.invoiceno ?? ""}
                    readOnly
                  />
                </div>

                <div className="as-csn_input-container">
                  <label className="as-csn_input-label">
                    {trans.purch_date}
                  </label>
                  <input
                    type="text"
                    className="as-csn_input-value"
                    id="reg_purch_date"
                    name="reg_purch_date"
                    value={formatDate(warrantyInfo.purchasedate) ?? ""}
                    readOnly
                  />
                </div>

                {/* Contact Details Section */}
                <div className="as-csn_section-title">
                  <div className="as-csn_icon-box" style={{ paddingRight: 10 }}>
                    <FallbackImg src={Human} alt="human" />
                  </div>
                  <span className="as-csn_section-text">
                    {trans.contact_details}
                  </span>
                </div>

                <div className="as-csn_sub-link as-csn_sub-indent">
                  {trans.contact_details_sub_1}
                </div>

                <div className="as-csn_input-box-container">
                  <div className="as-csn_section-text as-csn_field-label as-csn_required">
                    {trans.contact_name}
                  </div>
                  <input
                    type="text"
                    className="as-csn_input-box"
                    id="reg_custname"
                    name="reg_custname"
                    value={warrantyInfo.custname}
                    onChange={(e) => {
                      setWarrantyInfo((p) => ({
                        ...p,
                        custname: e.target.value,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        custname: "",
                      }));
                    }}
                    maxLength={100}
                  />
                  {errors.custname && (
                    <div className="as-error">{errors.custname}</div>
                  )}
                </div>

                <div className="as-csn_input-box-container">
                  <div className="as-csn_section-text as-csn_field-label as-csn_required">
                    {trans.mobile_no}
                  </div>
                  <div className="as-csn_phone-row">
                    <select
                      className="as-csn_input-box as-csn_phone-country-select"
                      value={phoneCountry}
                      onChange={(e) => handlePhoneCountryChange(e.target.value)}
                      aria-label="Country code"
                    >
                      {PHONE_COUNTRY_OPTIONS.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.dial}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className="as-csn_input-box as-csn_phone-number-input"
                      id="reg_custtelno"
                      name="reg_custtelno"
                      value={warrantyInfo.custtelno}
                      onChange={(e) => handlePhoneInput(e.target.value)}
                      onBlur={(e) => handlePhoneBlur(e.target.value)}
                      maxLength={100}
                      placeholder="Phone number"
                    />
                  </div>
                  {errors.custtelno && (
                    <div className="as-error">{errors.custtelno}</div>
                  )}
                </div>

                <div className="as-csn_input-box-container">
                  <div className="as-csn_section-text as-csn_field-label">
                    {trans.email_add}
                  </div>
                  <input
                    type="text"
                    className="as-csn_input-box"
                    id="reg_email"
                    name="reg_email"
                    value={warrantyInfo.emailadd}
                    onChange={(e) =>
                      setWarrantyInfo((p) => ({
                        ...p,
                        emailadd: e.target.value,
                      }))
                    }
                    maxLength={100}
                    placeholder="Optional"
                  />
                </div>

                <div className="as-csn_checkbox-row">
                  <input
                    type="checkbox"
                    id="agree_policy"
                    checked={agreeWarrantyPolicy}
                    onChange={(e) => setAgreeWarrantyPolicy(e.target.checked)}
                  />
                  <label htmlFor="agree_policy" style={{ fontSize: "13px" }}>
                    {trans.agree_1}
                    <a
                      href="#"
                      onClick={openPdf}
                      style={{ color: "#0B5ED7", textDecoration: "underline" }}
                    >
                      {trans.agree_2}
                    </a>
                    {trans.agree_3}
                    <a
                      href="https://file.unity.net.my/document/PDPA+acknowledgement.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#0B5ED7", textDecoration: "underline" }}
                    >
                      {trans.agree_4}
                    </a>
                  </label>
                </div>

                <button
                  type="button"
                  className="as-csn_primary-btn"
                  id="submit_btn"
                  onClick={handleSubmit}
                  disabled={!agreeWarrantyPolicy}
                >
                  {trans.submit}
                </button>
              </div>
              {/* FORM END */}
            </div>
          </div>
        </div>
      </div>

      {/* IMPORTANT: only after successful registration, localstorage is set */}
      {showSuccessMessage && (
        <RegisteredReceived
          isLocal={true}
          serialQuery={buildAfterSalesQuery(searchParams)}
        />
      )}
      {showMessage && (
        <MultiPurpose
          title="Registration Status"
          message={notifMessage}
          yesText="OK"
          hideNo={true}
          onYes={() => {
            setShowMessage(false);
            window.location.reload();
          }}
          onClose={() => {
            setShowMessage(false);
            window.location.reload();
          }}
        />
      )}
    </AfterSalesLayout>
  );
};

export default RegisterSerialNo;
